/** @file fitTargetsOf: the filled tiles a slot.fitPivot call measures, with entry and kind (CR-012). */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { deriveSlots } from '@zilo/pose3d-format/derive/deriveSlots';
import type { ImpostorDrawable } from '@zilo/pose3d-format/types/drawable';
import type { FacetEntry, ImageSlot } from '@zilo/pose3d-format/types/facet';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { findDrawable } from '../../drawable/findDrawable';
import type { PivotPartKind } from '../../types/puppetInputs';
import { findSlot } from '../findSlot';
import { pivotKindOf } from './pivotKindOf';

/** One tile to measure: its slot, the impostor and entry that own it, the image in it and the part kind. */
export type FitTarget = { slot: ImageSlot; drawable: ImpostorDrawable; entry: FacetEntry; image: string; kind: PivotPartKind };

/**
 * `slot` (one tile, which must be filled) or `drawable` (every filled tile of an impostor, at least one); exactly one
 * of the two (VALIDATION at `/input`). `kind` overrides `pivotKindOf` for every tile.
 */
export function fitTargetsOf(puppet: Puppet, input: { slot?: string | undefined; drawable?: string | undefined; kind?: PivotPartKind | undefined }): FitTarget[] {
  if ((input.slot === undefined) === (input.drawable === undefined)) {
    throwError(ERROR_CODES.VALIDATION, {}, { path: '/input', hint: 'name exactly one of slot (one tile) or drawable (every filled tile)' });
  }
  const slots = input.slot !== undefined ? [findSlot(puppet, input.slot).slot] : drawableSlots(puppet, input.drawable ?? '');
  return slots.map((slot): FitTarget => {
    const d = puppet.drawables.find((x) => x.id === slot.drawable) as ImpostorDrawable;
    const entry = d.facets[slot.entry] as FacetEntry;
    const image = entry.slots[String(slot.tile)];
    if (image === undefined) throwError(ERROR_CODES.VALIDATION, { slot: slot.key }, { path: '/input/slot', hint: 'fill the slot first: the pivot is measured from its art' });
    return { slot, drawable: d, entry, image, kind: input.kind ?? pivotKindOf(puppet, d, entry, slot.drawnScreenAngle) };
  });
}

/** The filled slots of impostor `id` (NOT_FOUND / VALIDATION at `/input/drawable`). */
function drawableSlots(puppet: Puppet, id: string): ImageSlot[] {
  const { drawable } = findDrawable(puppet, id);
  if (drawable.kind !== 'impostor') throwError(ERROR_CODES.VALIDATION, { kind: drawable.kind }, { path: '/input/drawable', hint: 'pivots are fitted on impostors' });
  const filled = deriveSlots(puppet).filter((s) => s.drawable === id && s.status === 'filled');
  if (filled.length === 0) throwError(ERROR_CODES.VALIDATION, { drawable: id }, { path: '/input/drawable', hint: 'fill its slots first: pivots are measured from the art' });
  return filled;
}
